// Collection bar sheets — the Filter and Sort boxes share one behaviour:
// a bottom sheet on mobile, an anchored dropdown box on desktop.
//
// Two things are worth knowing about the implementation:
//
// 1. Portalling. The panels render inside .collection-sortbar, which is
//    `position: sticky; z-index: 10` and therefore a stacking context —
//    so a panel's own z-index is trapped beneath it and the fixed site
//    header (z-index 20) paints over a full-bleed sheet. On mobile the
//    panel is moved to <body> so it escapes that context. On desktop it
//    stays put, because `position: absolute` has to resolve against the
//    trigger, and a dropdown hanging below the bar never reaches the
//    header anyway.
//
// 2. In-place updates. Choosing a value must not reload the page: a
//    reload closed the sheet and lost your place, so drilling meant
//    starting over. Links are intercepted and the section is re-fetched
//    through Shopify's Section Rendering API (the same `section_id`
//    mechanism collection-load-more.js uses), then applied piecemeal —
//    the sheet element itself is never replaced, which is what used to
//    flash the product grid mid-drill. Links stay real links, so this
//    degrades to plain navigation if the fetch fails or JS never runs.
const MOBILE_QUERY = '(max-width: 767px)';
const RESUME_KEY = 'sylvieFilterDrawerResume';

class SheetMenu extends HTMLElement {
	connectedCallback() {
		this.panel = this.querySelector('[data-sheet-panel]');
		this.trigger = this.querySelector('[data-action="toggle"]');
		if (!this.panel) return;
		// Captured before any portalling so the panel can be put back.
		this.homeParent = this.panel.parentElement;
		// Matches the collection grid and the search results section alike.
		this.section = this.closest('[data-section-id]');

		// Bound separately: once portalled the panel no longer bubbles into
		// this element, and while it is still inside, host-level handling has
		// to skip panel events or every click would be handled twice.
		this.onHostClick = (event) => {
			if (this.panel.contains(event.target)) return;
			this.handle(event);
		};
		this.onPanelClick = (event) => this.handle(event);
		this.onKeydown = (event) => {
			if (event.key === 'Escape' && !this.panel.hidden) this.close();
		};
		this.onDocumentClick = (event) => {
			if (this.panel.hidden) return;
			if (this.panel.contains(event.target) || this.contains(event.target)) return;
			this.close();
		};

		this.addEventListener('click', this.onHostClick);
		this.panel.addEventListener('click', this.onPanelClick);
		document.addEventListener('keydown', this.onKeydown);
		document.addEventListener('click', this.onDocumentClick);

		this.init?.();
	}

	disconnectedCallback() {
		document.removeEventListener('keydown', this.onKeydown);
		document.removeEventListener('click', this.onDocumentClick);
		// A portalled panel lives outside this element, so removing the
		// section wouldn't take it with it.
		if (this.panel && this.panel.parentElement === document.body) this.panel.remove();
		// Removed while open: nothing else will clear these, and a stale
		// sheetOpen keeps the chip row hidden.
		if (!document.querySelector('[data-sheet-panel]:not([hidden])')) {
			delete document.body.dataset.sheetOpen;
		}
		document.body.style.overflow = '';
	}

	isSamePath(href) {
		try {
			return new URL(href, window.location.origin).pathname === window.location.pathname;
		} catch (e) {
			return false;
		}
	}

	isMobile() {
		return window.matchMedia(MOBILE_QUERY).matches;
	}

	// Re-evaluated on every open so a resize between opens lands correctly.
	place() {
		const target = this.isMobile() ? document.body : this.homeParent;
		if (this.panel.parentElement !== target) target.appendChild(this.panel);
	}

	open() {
		this.place();
		this.panel.hidden = false;
		this.trigger?.setAttribute('aria-expanded', 'true');
		// Suppresses the chip row while open (see collection-results-bar).
		document.body.dataset.sheetOpen = '';
		if (this.isMobile()) document.body.style.overflow = 'hidden';
		this.afterOpen?.();
	}

	close() {
		this.panel.hidden = true;
		this.trigger?.setAttribute('aria-expanded', 'false');
		if (!document.querySelector('[data-sheet-panel]:not([hidden])')) {
			delete document.body.dataset.sheetOpen;
		}
		document.body.style.overflow = '';
	}

	toggle() {
		if (this.panel.hidden) this.open();
		else this.close();
	}

	handle(event) {
		const link = event.target.closest('[data-sheet-link]');
		// Only swap the section in place when the link stays on this template.
		// Clearing a search term sends you to /collections/all, a different
		// template whose section id differs — fetching this section against
		// that path would return the wrong markup, so let it navigate.
		if (link && link.href && this.section?.dataset.sectionId && this.isSamePath(link.href)) {
			event.preventDefault();
			this.navigate(link.href);
			return;
		}
		const target = event.target.closest('[data-action]');
		if (!target) return;
		const { action } = target.dataset;
		if (action === 'toggle') this.toggle();
		else if (action === 'close') this.close();
		else this.onAction?.(action, target);
	}

	async navigate(href) {
		if (this.loading) return;
		this.loading = true;
		this.panel.setAttribute('data-loading', '');

		const url = new URL(href, window.location.origin);
		url.searchParams.set('section_id', this.section.dataset.sectionId);

		try {
			const res = await fetch(url.toString());
			if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
			const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

			const clean = new URL(href, window.location.origin);
			clean.searchParams.delete('section_id');
			history.pushState({}, '', clean.pathname + clean.search);

			this.applySection(doc);
			this.loading = false;
			this.panel.removeAttribute('data-loading');
			this.afterNavigate?.();
		} catch (e) {
			// Fall back to a normal page load.
			this.loading = false;
			this.panel.removeAttribute('data-loading');
			try {
				sessionStorage.setItem(RESUME_KEY, '1');
			} catch (err) {
				/* no resume; the sheet just opens closed next load */
			}
			window.location.href = href;
		}
	}

	// Swap in the freshly rendered pieces without replacing either sheet
	// element, so open panels and their listeners survive.
	applySection(doc) {
		const swapInner = (selector, root = document) => {
			const next = doc.querySelector(selector);
			const current = root.querySelector(selector);
			if (next && current) current.innerHTML = next.innerHTML;
		};

		// Each sheet (Shop by Category, Filter & Sort) refreshes its own
		// contents and trigger. Keyed rather than hard-coded so adding a
		// third control needs no change here.
		doc.querySelectorAll('[data-sheet-key]').forEach((nextSheet) => {
			const key = nextSheet.dataset.sheetKey;
			const current = document.querySelector(`[data-sheet-key="${key}"]`);
			if (!current) return;
			current.dataset.defaultScreen = nextSheet.dataset.defaultScreen || '';
			const nextInner = nextSheet.querySelector('.filterdrawer__inner');
			const inner = current.querySelector('.filterdrawer__inner');
			if (nextInner && inner) inner.innerHTML = nextInner.innerHTML;
			const nextTrigger = nextSheet.querySelector('.cbox__trigger');
			const trigger = current.querySelector('.cbox__trigger');
			if (nextTrigger && trigger) trigger.innerHTML = nextTrigger.innerHTML;
		});

		// Results. collection-load-more keeps its element identity, so its
		// grid reference stays valid; its saved-state guard fails safe when
		// the page key no longer matches.
		swapInner('.search-summary'); // search only; no-op on collections
		swapInner('.collection-chips');

		// The search box is deliberately NOT swapped wholesale — that would
		// discard anything typed but not yet submitted. Only its clear link,
		// whose href carries the current filters, is reconciled.
		const nextClear = doc.querySelector('.cbox__clear');
		const clear = document.querySelector('.cbox__clear');
		const form = document.querySelector('.cbox--search');
		if (nextClear && clear) clear.setAttribute('href', nextClear.getAttribute('href'));
		else if (nextClear && form) form.appendChild(nextClear);
		else if (clear) clear.remove();

		swapInner('.collection-grid');
		const nextMore = doc.querySelector('.collection-loadmore');
		const more = document.querySelector('.collection-loadmore');
		if (more && nextMore) more.replaceWith(nextMore);
		else if (more) more.remove();
		else if (nextMore) document.querySelector('.collection__grid-wrap')?.after(nextMore);
	}
}

class FilterDrawer extends SheetMenu {
	init() {
		// The chip row and the search box's clear "x" sit outside this element
		// and outside the panel, so their links would otherwise be plain
		// navigations. Bound by exactly one sheet — there are two on the page
		// now, and binding in both would fire two fetches per click.
		if (this.dataset.sheetKey === 'filters') {
			// Delegated from the containers, not the links themselves:
			// applySection replaces both the chips' innerHTML and (when a
			// search term appears) the clear link itself, so a listener
			// bound to a link would be dropped on the first update. The
			// containers outlive every swap. handle() ignores anything
			// without [data-sheet-link], so the search field and its
			// submit button are unaffected.
			this.outside = this.section?.querySelector('.collection-chips');
			this.searchForm = this.section?.querySelector('.cbox--search');
			if (this.outside) this.outside.addEventListener('click', this.onPanelClick);
			if (this.searchForm) this.searchForm.addEventListener('click', this.onPanelClick);
		}

		// Range sliders: drag to preview, release to apply.
		this.panel.addEventListener('input', (event) => {
			const input = event.target.closest('[data-range-lo], [data-range-hi]');
			if (input) this.onRangeInput(input);
		});
		this.panel.addEventListener('change', (event) => {
			const input = event.target.closest('[data-range-lo], [data-range-hi]');
			if (input) this.onRangeCommit(input);
		});
		this.syncRanges();

		try {
			if (sessionStorage.getItem(RESUME_KEY) === '1') {
				sessionStorage.removeItem(RESUME_KEY);
				this.open();
			}
		} catch (e) {
			/* privacy mode; nothing to resume */
		}
	}

	// A taxonomy screen advances to the next level after a selection —
	// that's what data-default-screen encodes. Everywhere else (sort, a
	// range, a value list) you stay put so you can keep adjusting without
	// being thrown out of the screen you're working in.
	//
	// Which screens advance is marked in the markup with data-advances
	// rather than listed here, so adding a taxonomy level doesn't mean
	// remembering to edit this file too.
	handle(event) {
		const link = event.target.closest('[data-sheet-link]');
		if (link) {
			const screen = link.closest('.filterdrawer__screen');
			const id = screen ? screen.dataset.screenId || '' : '';
			this.stickScreen = id && !screen.hasAttribute('data-advances') ? id : null;
		}
		super.handle(event);
	}

	afterOpen() {
		this.showScreen(this.dataset.entryScreen || 'root', 'forward');
	}

	afterNavigate() {
		// Adjusting a slider shouldn't eject you from that slider's screen,
		// which is where data-default-screen would otherwise send you.
		const stick = this.stickScreen;
		this.stickScreen = null;
		this.showScreen(stick || this.dataset.defaultScreen, 'forward');
		this.syncRanges();
	}

	syncRanges() {
		this.panel.querySelectorAll('[data-range]').forEach((root) => this.paintRange(root));
	}

	formatRangeValue(root, value) {
		return (root.dataset.prefix || '') + Number(value).toLocaleString() + (root.dataset.suffix || '');
	}

	paintRange(root) {
		const lo = root.querySelector('[data-range-lo]');
		const hi = root.querySelector('[data-range-hi]');
		if (!lo || !hi) return;
		const min = Number(root.dataset.min);
		const max = Number(root.dataset.max);
		const a = Number(lo.value);
		const b = Number(hi.value);
		const span = max - min || 1;
		const fill = root.querySelector('[data-range-fill]');
		if (fill) {
			fill.style.left = ((a - min) / span) * 100 + '%';
			fill.style.right = 100 - ((b - min) / span) * 100 + '%';
		}
		const out = root.querySelector('[data-range-readout]');
		if (out) {
			out.textContent = this.formatRangeValue(root, a) + ' – ' + this.formatRangeValue(root, b);
		}
	}

	// Keep the handles from crossing over each other.
	onRangeInput(input) {
		const root = input.closest('[data-range]');
		if (!root) return;
		const lo = root.querySelector('[data-range-lo]');
		const hi = root.querySelector('[data-range-hi]');
		if (Number(lo.value) > Number(hi.value)) {
			if (input === lo) lo.value = hi.value;
			else hi.value = lo.value;
		}
		this.paintRange(root);
	}

	onRangeCommit(input) {
		const root = input.closest('[data-range]');
		if (!root) return;
		const min = Number(root.dataset.min);
		const max = Number(root.dataset.max);
		const a = Number(root.querySelector('[data-range-lo]').value);
		const b = Number(root.querySelector('[data-range-hi]').value);

		const url = new URL(window.location.href);
		const listParam = root.dataset.paramList;
		if (listParam) {
			// Shopify exposes these as a value list, not a range, so the span
			// is expressed as every value inside it — repeated params, which
			// Shopify ORs together.
			url.searchParams.delete(listParam);
			if (a > min || b < max) {
				(root.dataset.values || '')
					.split(',')
					.map((pair) => {
						const [num, encoded] = pair.split(':');
						// Decoded here because searchParams re-encodes on append.
						return { n: Number(num), value: decodeURIComponent((encoded || '').replace(/\+/g, ' ')) };
					})
					.filter((item) => Number.isFinite(item.n) && item.n >= a && item.n <= b && item.value !== '')
					.forEach((item) => url.searchParams.append(listParam, item.value));
			}
		} else {
			// A handle parked at its bound isn't a constraint — drop the param
			// so the URL (and the chip row) don't claim a filter that isn't
			// narrowing anything.
			if (a > min) url.searchParams.set(root.dataset.paramMin, a);
			else url.searchParams.delete(root.dataset.paramMin);
			if (b < max) url.searchParams.set(root.dataset.paramMax, b);
			else url.searchParams.delete(root.dataset.paramMax);
		}

		const screen = this.panel.querySelector('.filterdrawer__screen:not([hidden])');
		this.stickScreen = screen ? screen.dataset.screenId : null;
		this.navigate(url.pathname + url.search);
	}

	onAction(action, target) {
		if (action === 'show') this.showScreen(target.dataset.showScreen, 'back');
	}

	showScreen(id, direction) {
		const screens = this.panel.querySelectorAll('.filterdrawer__screen');
		if (!screens.length) return;
		// Fall back to the first screen if the requested one isn't present,
		// so the sheet is never shown with every screen hidden.
		const exists = id && this.panel.querySelector(`.filterdrawer__screen[data-screen-id="${id}"]`);
		const targetId = exists ? id : screens[0].dataset.screenId;
		this.panel.dataset.direction = direction === 'back' ? 'back' : 'forward';
		screens.forEach((screen) => {
			screen.hidden = screen.dataset.screenId !== targetId;
		});
	}
}

// Mobile shows only a search icon so Search / Filter / Sort fit on one line;
// expanding gives the field the full row and pushes Filter and Sort below.
// Above 768px the CSS keeps the field open and hides the toggle, so this
// element does nothing there.
class SearchBox extends HTMLElement {
	connectedCallback() {
		this.input = this.querySelector('[data-search-input]');
		const toggle = this.querySelector('[data-search-toggle]');
		if (!toggle || !this.input) return;

		toggle.addEventListener('click', () => {
			this.toggleAttribute('data-expanded', true);
			toggle.setAttribute('aria-expanded', 'true');
			this.input.focus();
		});

		// Collapse again on blur only when nothing was typed, so an empty box
		// doesn't sit open over the controls.
		this.input.addEventListener('blur', () => {
			if (this.input.value.trim() !== '') return;
			this.removeAttribute('data-expanded');
			toggle.setAttribute('aria-expanded', 'false');
		});
	}
}

customElements.define('search-box', SearchBox);
// Both sheets share one implementation; their markup decides the screens.
// customElements.define rejects a constructor that is already registered,
// so the second tag needs its own (empty) subclass rather than a second
// reference to FilterDrawer.
customElements.define('category-menu', FilterDrawer);
customElements.define('filter-sort', class extends FilterDrawer {});
